import { useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader } from 'components/Loader';
import { ProductGlobalForm } from './ProductGlobalForm';

import {
    useGetSuppliersQuery,
    useGetActiveCategoriesQuery,
    useGetActiveOrganizationsQuery,
    useGetDetailedProductQuery,
    useGetProductPriceRangesQuery,
    useGetProductPricesQuery,
    useGetTagsQuery,
    useUpdatedProductSubscription,
} from 'data/__generated__';

type ParamTypes = {
    productId: string;
};

export const ProductDetails = () => {
    const { productId = '' } = useParams<ParamTypes>();

    const { data: subscriptionData } = useUpdatedProductSubscription({
        variables: { productId },
    });

    useEffect(() => {
        if (subscriptionData?.updatedProduct) {
            toast.warn("Le produit vient d'être mis à jour");
        }
    }, [subscriptionData]);

    const {
        loading: productLoading,
        data: productData,
        error: productError,
    } = useGetDetailedProductQuery({
        variables: { productId },
    });

    const {
        loading: activeCategoriesLoading,
        data: activeCategoriesData,
        error: activeCategoriesError,
    } = useGetActiveCategoriesQuery();

    const { loading: tagsLoading, data: tagsData, error: tagsError } = useGetTagsQuery();

    const {
        loading: productRangesLoading,
        data: productRangesData,
        error: productRangesError,
    } = useGetProductPriceRangesQuery();

    const {
        loading: productPricesLoading,
        data: productPricesData,
        error: productPricesError,
    } = useGetProductPricesQuery({
        variables: { productId },
    });

    const {
        loading: organizationsLoading,
        data: organizationsData,
        error: organizationsError,
    } = useGetActiveOrganizationsQuery();

    const { loading: suppliersLoading, data: suppliersData, error: suppliersError } = useGetSuppliersQuery();

    if (
        organizationsLoading ||
        productLoading ||
        productPricesLoading ||
        productRangesLoading ||
        activeCategoriesLoading ||
        tagsLoading ||
        suppliersLoading
    ) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (
        organizationsError ||
        !organizationsData ||
        productError ||
        !productData ||
        productRangesError ||
        !productRangesData ||
        productPricesError ||
        !productPricesData ||
        activeCategoriesError ||
        !activeCategoriesData ||
        tagsError ||
        !tagsData ||
        suppliersError ||
        !suppliersData
    ) {
        throw new Error('Une erreur est survenue lors de la récupération du produit');
    }
    const { tags } = tagsData;
    const { detailedProduct } = productData;
    const { activeCategories } = activeCategoriesData;
    const { activeOrganizations } = organizationsData;
    const { productPriceRanges } = productRangesData;
    const { productPrices } = productPricesData;
    const { suppliers } = suppliersData;

    if (!detailedProduct) {
        throw new Error(`Aucun produit avec l'id ${productId} n'a été trouvé`);
    }

    return (
        <Container>
            <ProductGlobalForm
                key={detailedProduct.updatedAt}
                tags={tags}
                categories={activeCategories}
                organizations={activeOrganizations}
                detailedProduct={detailedProduct}
                productPrices={productPrices}
                productPriceRanges={productPriceRanges}
                suppliers={suppliers}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
